import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Typography,
  Tag,
  message,
} from 'antd';

import { ApiError } from '../../../shared/api-client/index.js';
import { useScenario } from '../hooks/useScenario.js';
import { useDuplicateScenario, usePatchScenario } from '../hooks/useScenarioMutations.js';
import type { ScenarioDetail } from '../api/automations.client.js';

const { Text } = Typography;

interface ScenarioEditorDrawerProps {
  scenarioId: string | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

function useDrawerWidth(): number | string {
  const [width, setWidth] = useState<number | string>(520);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => {
      setWidth(window.innerWidth < 640 ? '100vw' : 520);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return width;
}

function extractConfigJson(config: unknown): string {
  try {
    return JSON.stringify(config, null, 2);
  } catch {
    return String(config);
  }
}

function parseAdvancedJson(
  value: string,
): { ok: true; data: unknown } | { ok: false; error: string } {
  if (value.trim() === '') return { ok: true, data: undefined };
  try {
    const data = JSON.parse(value) as unknown;
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'invalid json' };
  }
}

export function ScenarioEditorDrawer({
  scenarioId,
  open,
  onClose,
  onSaved,
}: ScenarioEditorDrawerProps): ReactElement {
  const { t } = useTranslation();
  const drawerWidth = useDrawerWidth();
  const { data: scenario, isLoading, isError, error, refetch } = useScenario(scenarioId);
  const patch = usePatchScenario();
  const duplicate = useDuplicateScenario();
  const [form] = Form.useForm();
  const [advancedJson, setAdvancedJson] = useState('');
  const [advancedError, setAdvancedError] = useState<string | null>(null);

  const labels = useMemo(
    () => ({
      drawerTitle: t('crm.scenario_editor.drawer_title'),
      drawerSubtitle: (name: string, version: number) =>
        t('crm.scenario_editor.drawer_subtitle', { name, version }),
      loading: t('crm.scenario_editor.loading'),
      loadError: t('crm.scenario_editor.load_error'),
      retry: t('crm.scenario_editor.retry'),
      save: t('crm.scenario_editor.save'),
      saving: t('crm.scenario_editor.saving'),
      saveSuccess: (version: number) => t('crm.scenario_editor.save_success', { version }),
      saveError: t('crm.scenario_editor.save_error'),
      validationError: t('crm.scenario_editor.validation_error'),
      versionConflict: t('crm.scenario_editor.version_conflict'),
      forbidden: t('crm.scenario_editor.forbidden'),
      upgradeNeeded: t('crm.scenario_editor.upgrade_needed'),
      duplicate: t('crm.scenario_editor.duplicate'),
      duplicateSuccess: t('crm.scenario_editor.duplicate_success'),
      close: t('crm.scenario_editor.close'),
      cancel: t('crm.scenario_editor.cancel'),
      name: t('crm.scenario_editor.field.name'),
      namePlaceholder: t('crm.scenario_editor.field.name_placeholder'),
      description: t('crm.scenario_editor.field.description'),
      trigger: t('crm.scenario_editor.field.trigger'),
      triggerDealStage: t('crm.scenario_editor.field.trigger_deal_stage'),
      triggerManual: t('crm.scenario_editor.field.trigger_manual'),
      triggerScheduled: t('crm.scenario_editor.field.trigger_scheduled'),
      triggerEvent: t('crm.scenario_editor.field.trigger_event'),
      waitDuration: t('crm.scenario_editor.field.wait_duration'),
      wait3d: t('crm.scenario_editor.field.wait_3d'),
      steps: t('crm.scenario_editor.field.steps'),
      stepWait: t('crm.scenario_editor.field.step_wait'),
      stepTool: t('crm.scenario_editor.field.step_tool'),
      version: t('crm.scenario_editor.field.version'),
      isActive: t('crm.scenario_editor.field.isActive'),
      advancedJson: t('crm.scenario_editor.field.advanced_json'),
      advancedHint: t('crm.scenario_editor.field.advanced_json_hint'),
      configLabel: t('crm.scenario_editor.config_label'),
      configHint: t('crm.scenario_editor.config_hint'),
      presetKeyLabel: t('crm.scenario_editor.preset_key_label'),
    }),
    [t],
  );

  useEffect(() => {
    if (scenario) {
      form.setFieldsValue({
        name: scenario.name,
        description: scenario.description ?? '',
        triggerType: scenario.triggerType,
      });
      setAdvancedJson(extractConfigJson(scenario.config));
      setAdvancedError(null);
    } else {
      form.resetFields();
      setAdvancedJson('');
      setAdvancedError(null);
    }
  }, [scenario, form]);

  const handleSave = async () => {
    if (!scenarioId || !scenario) return;
    try {
      const values = await form.validateFields();
      const configParsed = parseAdvancedJson(advancedJson);
      if (!configParsed.ok) {
        setAdvancedError(configParsed.error);
        return;
      }
      setAdvancedError(null);

      let configToSend: unknown | undefined;
      if (advancedJson.trim() !== '') {
        configToSend = configParsed.data;
      }

      // If advanced JSON is provided, use it as config. Otherwise keep existing config.
      // Also validate that config contains id/triggers/steps when provided.
      const payload: Record<string, unknown> = {
        expectedVersion: scenario.version,
      };
      if (values.name !== undefined) payload['name'] = values.name as string;
      if (values.description !== undefined)
        payload['description'] = (values.description as string) || null;
      if (values.triggerType !== undefined) payload['triggerType'] = values.triggerType;
      if (configToSend !== undefined) payload['config'] = configToSend;

      const result = (await patch.mutateAsync({
        id: scenarioId,
        payload: payload as unknown as Parameters<typeof patch.mutateAsync>[0]['payload'],
      })) as ScenarioDetail;
      message.success(labels.saveSuccess(result.version));
      onSaved?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          message.error(labels.versionConflict);
        } else if (err.status === 402) {
          message.error(labels.upgradeNeeded);
        } else if (err.status === 403) {
          message.error(labels.forbidden);
        } else if (err.status === 400) {
          message.error(labels.validationError);
        } else {
          message.error(labels.saveError);
        }
      } else if (err && typeof err === 'object' && 'errorFields' in err) {
        // antd form validation error - keep drawer open
        return;
      } else {
        message.error(labels.saveError);
      }
    }
  };

  const handleDuplicate = async () => {
    if (!scenarioId) return;
    try {
      await duplicate.mutateAsync(scenarioId);
      message.success(labels.duplicateSuccess);
      onSaved?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        message.error(labels.forbidden);
      } else {
        message.error(labels.saveError);
      }
    }
  };

  const footer = (
    <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Button onClick={onClose} aria-label={labels.close}>
        {labels.cancel}
      </Button>
      <Space>
        <Button
          onClick={handleDuplicate}
          loading={duplicate.isPending}
          aria-label={labels.duplicate}
        >
          {labels.duplicate}
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={patch.isPending}
          aria-label={labels.save}
        >
          {patch.isPending ? labels.saving : labels.save}
        </Button>
      </Space>
    </Space>
  );

  return (
    <Drawer
      title={
        scenario ? (
          <Space orientation="vertical" size={0}>
            <Text strong>{labels.drawerTitle}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {labels.drawerSubtitle(scenario.name, scenario.version)} · {labels.presetKeyLabel}:{' '}
              {scenario.presetKey ?? '—'}
            </Text>
          </Space>
        ) : (
          labels.drawerTitle
        )
      }
      placement="right"
      width={drawerWidth}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={null}
      footer={footer}
      aria-label={labels.drawerTitle}
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          {/* antd 6: Spin `tip` → `description`. */}
          <Spin description={labels.loading} />
        </div>
      ) : isError ? (
        <ErrorState
          title={labels.loadError}
          error={error instanceof Error ? error : String(error)}
          onRetry={() => void refetch()}
        />
      ) : scenario ? (
        <Form form={form} layout="vertical" initialValues={{ name: scenario.name }}>
          <Form.Item
            label={labels.name}
            name="name"
            rules={[{ required: true, message: labels.validationError }]}
          >
            <Input
              id="scenario-name"
              placeholder={labels.namePlaceholder}
              aria-label={labels.name}
            />
          </Form.Item>

          <Form.Item label={labels.description} name="description">
            <Input.TextArea
              id="scenario-description"
              rows={2}
              placeholder={labels.description}
              aria-label={labels.description}
            />
          </Form.Item>

          <Form.Item label={labels.trigger} name="triggerType">
            <Select
              id="scenario-trigger"
              aria-label={labels.trigger}
              options={[
                { value: 'event', label: labels.triggerDealStage },
                { value: 'manual', label: labels.triggerManual },
                { value: 'scheduled', label: labels.triggerScheduled },
              ]}
            />
          </Form.Item>

          <Space orientation="vertical" size={4} style={{ width: '100%', marginBottom: 16 }}>
            <Text strong>{labels.steps}</Text>
            <Tag>
              {labels.stepWait}: {labels.wait3d}
            </Tag>
            <Tag>{labels.stepTool}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {labels.configHint}
            </Text>
          </Space>

          <Form.Item label={labels.advancedJson} help={labels.advancedHint}>
            <Input.TextArea
              id="scenario-config-json"
              rows={10}
              value={advancedJson}
              onChange={(e) => setAdvancedJson(e.target.value)}
              placeholder={labels.configLabel}
              aria-label={labels.configLabel}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            {advancedError ? (
              <Alert
                type="error"
                showIcon
                title={labels.validationError}
                description={advancedError}
                style={{ marginTop: 8 }}
              />
            ) : null}
          </Form.Item>

          <Space orientation="vertical" size={2} style={{ width: '100%', marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {labels.version}: <Tag>{scenario.version}</Tag> · {labels.isActive}:{' '}
              <EntityStatusChip tone={scenario.isActive ? 'success' : 'default'}>
                {scenario.isActive ? 'active' : 'inactive'}
              </EntityStatusChip>
            </Text>
          </Space>

          {patch.isError ? (
            <Alert
              type="error"
              showIcon
              title={labels.saveError}
              description={patch.error instanceof Error ? patch.error.message : String(patch.error)}
              style={{ marginTop: 16 }}
            />
          ) : null}
        </Form>
      ) : null}
    </Drawer>
  );
}
import { ErrorState } from '../../../shared/ui/ErrorState.js';
